// Single source of truth for backend calls. All requests go through /api/*
// which the dev proxy (vite) and the prod nginx route to the Go backend.

import { passwortAbleiten } from "./lib/passwort";
import { NICHT_BEHANDELT, debugAntwort } from "./debug";

const TOKEN_KEY = "home.token";

// Wer angemeldet ist, entscheidet inzwischen, was die Oberfläche überhaupt
// zeigt — ohne Anmeldung gibt es ein öffentliches Dashboard statt einer
// Weiterleitung. Damit ein Login mitten auf der Seite sofort wirkt, ohne
// Neuladen, ist der Zustand beobachtbar.
const authListeners = new Set<() => void>();

/** Meldet sich für Änderungen an der Anmeldung an. Gibt die Abmeldung zurück. */
export function onAuthChange(fn: () => void): () => void {
  authListeners.add(fn);
  return () => authListeners.delete(fn);
}

function notifyAuth() {
  for (const fn of authListeners) fn();
}

export function getToken(): string | null {
  // Im Debug-Modus ist man immer angemeldet: sonst stuende man vor dem
  // Anmeldefenster und saehe genau die Seite nicht, die man ansehen wollte.
  if (import.meta.env.VITE_DEBUG_MODE === "true") return "debug-token";
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(t: string) {
  const vorher = localStorage.getItem(TOKEN_KEY);
  localStorage.setItem(TOKEN_KEY, t);
  // Der Sliding-Refresh schreibt bei fast jeder Anfrage einen neuen Token.
  // Nur ein Wechsel zwischen "no token" und "Token" ändert die Sicht —
  // sonst würde jede Anfrage einen Rerender der ganzen App auslösen.
  if (!vorher) notifyAuth();
}

export function clearToken() {
  const vorher = localStorage.getItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
  // Mit der Anmeldung geht auch, wer man war. Sonst sähe der Nächste an
  // diesem Browser die Leiste des Vorigen.
  try { localStorage.removeItem(ICH_KEY); } catch { /* egal */ }
  ich = null;
  for (const fn of ichHoerer) fn();
  if (vorher) notifyAuth();
}

// ─────────────────────────── Step-up auth ───────────────────────────
// Destructive endpoints answer 403 STEP_UP_REQUIRED when called with a plain
// session token. We obtain a short-lived step-up token (password + 2FA) and
// retry the request with the X-Step-Up header. The token is cached for its
// lifetime so a burst of sensitive actions only prompts once.

type StepUpProvider = () => Promise<string | null>;
let stepUpProvider: StepUpProvider | null = null;

/** Registered once by <StepUpGate>; opens the password/2FA modal on demand. */
export function setStepUpProvider(p: StepUpProvider | null) {
  stepUpProvider = p;
}

/* ─── Ist der Server ueberhaupt da? ──────────────────────────────────
 *
 * Eine fehlgeschlagene Anfrage kann zweierlei heissen: der Server sagt nein
 * (dann hat sie einen Status), oder es kam gar nichts (dann nicht). Das
 * zweite ist kein Fehler einer einzelnen Seite, sondern ein Zustand der
 * ganzen Anwendung — waehrend eines Neustarts etwa, der zwei bis vier
 * Minuten dauert.
 *
 * Deshalb steht er hier und nicht in jeder Seite einzeln. Die Oberflaeche
 * haengt sich daran (components/ServerOffline) und legt sich darueber,
 * sobald es still wird.
 */
let serverIstWeg = false;
const wegHoerer = new Set<() => void>();

export function onServerWeg(fn: () => void): () => void {
  wegHoerer.add(fn);
  return () => { wegHoerer.delete(fn); };
}

export function istServerWeg(): boolean { return serverIstWeg; }

function serverWeg(weg: boolean) {
  if (serverIstWeg === weg) return;
  serverIstWeg = weg;
  for (const fn of wegHoerer) fn();
}

/** Nachsehen, ob er wieder da ist. Fuer die Anzeige, die es regelmaessig
 *  versucht — ohne Token und ohne Umwege, damit auch eine abgelaufene
 *  Anmeldung nicht dazwischenfunkt. */
export async function serverLebt(): Promise<boolean> {
  try {
    const r = await fetch("/api/health", { cache: "no-store" });
    serverWeg(!r.ok && r.status === 0);
    if (r.ok) serverWeg(false);
    return r.ok;
  } catch {
    serverWeg(true);
    return false;
  }
}

let cachedStepUp: { token: string; exp: number } | null = null;

type StepUpInfo = { token?: string; prompted?: boolean };

export async function api<T>(
  path: string,
  init: RequestInit = {},
  su?: StepUpInfo,
): Promise<T> {
  // Bei FormData MUSS der Browser den Content-Type selbst setzen: nur er
  // kennt die Boundary des multipart-Bodies. Wurde hier pauschal
  // "application/json" gesetzt, kam beim Server ein Body ohne Boundary an —
  // Fiber fand kein Feld und antwortete "missing file". Genau das war der
  // Grund, warum der Upload über die Oberfläche nie funktioniert hat.
  // Ohne Server: die Antwort kommt aus den erfundenen Daten (siehe ./debug).
  // Der Vergleich steht als Literal da, damit der Buendler den Zweig in einem
  // echten Bau samt Daten entfernt.
  if (import.meta.env.VITE_DEBUG_MODE === "true") {
    const erfunden = debugAntwort(path, init);
    if (erfunden !== NICHT_BEHANDELT) {
      await new Promise((r) => setTimeout(r, 80));
      return erfunden as T;
    }
    throw new ApiError(404, "DEBUG_UNHANDLED", `debug mode has no answer for ${path}`);
  }

  const isForm = init.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isForm ? {} : { "Content-Type": "application/json" }),
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (isForm) delete headers["Content-Type"];
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (su?.token) headers["X-Step-Up"] = su.token;

  let res: Response;
  try {
    res = await fetch(`/api${path}`, { ...init, headers });
  } catch (e) {
    // Kein Status, keine Antwort: der Server war nicht da.
    //
    // Das ist etwas anderes als ein Fehler *vom* Server, und es hat bis
    // hierher wie einer ausgesehen — jede Seite zeigte ihre eigene rote
    // Zeile, und man raetselte, ob man etwas falsch gemacht hat. Ab hier
    // weiss die Oberflaeche es zentral und sagt es einmal, gross.
    serverWeg(true);
    throw new ApiError(0, "OFFLINE",
      navigator.onLine
        ? "The server is not answering."
        : "This device has no connection.");
  }
  // Eine Antwort — irgendeine — heisst: er ist wieder da. Auch ein 500 ist
  // ein Lebenszeichen; nur die Stille ist es nicht.
  serverWeg(false);
  const refreshed = res.headers.get("X-Refresh-Token");
  if (refreshed) setToken(refreshed);
  const text = await res.text();

  // Defensively parse: a flaky proxy / overloaded backend can return HTML
  // (502 / 504 / nginx error page) instead of JSON. JSON.parse on that
  // throws a cryptic "unexpected character at line 1 column 1" — surface
  // a useful error instead so the caller can decide what to do.
  let body: { code?: string; message?: string } | null = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // Keine JSON-Antwort heißt fast immer: der Server war nicht der
      // Server, sondern etwas davor — die Fehlerseite eines Zwischenspeichers
      // oder des Tunnels. Sie hier hineinzukopieren half niemandem: der
      // Nutzer sah zwei Zeilen HTML und wusste weniger als vorher.
      const html = /^\s*<(!doctype|html)/i.test(text);
      throw new ApiError(
        res.status || 0,
        res.ok ? "BAD_RESPONSE" : "ERROR",
        html
          ? (res.status === 502 || res.status === 503 || res.status === 504
              ? "The server did not answer in time. It may be busy or restarting."
              : `The server answered with a page instead of data (${res.status}).`)
          : `Unexpected answer (${res.status}): ${text.slice(0, 120).trim() || res.statusText}`,
      );
    }
  }

  if (!res.ok) {
    // The action needs re-authentication. Try a still-valid cached step-up
    // token first, then prompt the user — each at most once, so a token the
    // server has already expired can't trigger an infinite retry loop.
    if (res.status === 403 && body?.code === "STEP_UP_REQUIRED") {
      if (!su?.token && !su?.prompted && cachedStepUp && cachedStepUp.exp > Date.now()) {
        return api<T>(path, init, { token: cachedStepUp.token });
      }
      if (!su?.prompted && stepUpProvider) {
        cachedStepUp = null;
        const fresh = await stepUpProvider();
        if (fresh) return api<T>(path, init, { token: fresh, prompted: true });
      }
    }
    // A dead session on any non-auth endpoint (expired, binding mismatch,
    // revoked via kill-switch) → drop the stale token. Kein Sprung auf
    // /login mehr: die abgemeldete Oberfläche zeigt die Seite gesperrt mit
    // einem Anmelde-Knopf, und nach der Anmeldung steht man wieder genau
    // hier. Auth endpoints are excluded so a wrong password/2FA code surfaces
    // in the form instead of logging the user out mid-attempt.
    //
    // Aber nur, wenn die Absage auch dem Schlüssel gilt, mit dem gerade
    // gearbeitet wird. Eine Anfrage, die noch mit dem alten Schlüssel
    // unterwegs war, als man sich schon neu angemeldet hat, kommt
    // zwangsläufig mit 401 zurück — und warf bis eben die *frische*
    // Anmeldung weg. Wer zwei Fenster offen hat oder gerade sein Passwort
    // geändert hat, landete so in der Schleife „anmelden, abgemeldet
    // werden, anmelden".
    //
    // Und nur, wenn die Absage *dieser Anmeldung* gilt. Eine 401 heisst
    // „Anmeldung fehlgeschlagen" — sie sagt aber nicht, bei welcher Anmeldung.
    // Der Server fragt an zwei Stellen nach einem ganz anderen Passwort: nach
    // dem SSH-Passwort der Maschine und nach dem Loeschwort eines oeffentlichen
    // Moduls. Beide antworten mit 401, und beide warfen bis hier die
    // Sitzung der Website weg.
    //
    // Das war der gemeldete Fehler: ein falsch getipptes SSH-Passwort meldete
    // einen von der ganzen Seite ab („i cant log into machine. i get logged
    // out directly"). Ein fremdes Schloss, derselbe Schluesselbund.
    //
    // Umgekehrt bleibt die Vorgabe streng: ein unbekannter Fehlercode und
    // eine 401 ganz ohne Antwortkoerper — etwa vom Tunnel davor — raeumen den
    // Schluessel weiterhin weg. Einen toten Schluessel zu behalten waere der
    // schlimmere Fehler: man kaeme nicht mehr heraus.
    if (res.status === 401 && token && getToken() === token
        && !path.startsWith("/auth/")
        // Fehlt der Code ganz, wird daraus "" — das steht in keiner Liste,
        // und der Schluessel fliegt wie bisher raus.
        && !FREMDES_SCHLOSS.has(body?.code ?? "")) {
      clearToken();
    }
    throw new ApiError(res.status, body?.code ?? "ERROR", body?.message ?? res.statusText);
  }
  return body as T;
}

/** 401-Codes, die einem *anderen* Passwort gelten als dem der Sitzung.
 *
 *  Der Server schickt den Code mit; bis hier las nur niemand ihn. Wer hier
 *  etwas ergaenzt, muss sicher sein, dass der Code wirklich nie fuer eine
 *  abgelaufene Sitzung steht — sonst bleibt ein toter Schluessel liegen. */
const FREMDES_SCHLOSS = new Set([
  // Das SSH-Passwort der Maschine (POST /pc/sessions und Geschwister).
  "SSH_AUTH",
  // Das Loeschwort eines oeffentlichen Moduls.
  "PASSWORD_REQUIRED", "WRONG_PASSWORD",
]);

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

export type LoginOk = { token: string; expires_in: number };
export type LoginChallenge = { step: "2fa"; challenge: string };
export type LoginResult = LoginOk | LoginChallenge;

function isChallenge(r: LoginResult): r is LoginChallenge {
  return "step" in r && r.step === "2fa";
}

/** Anmelden. Der Name darf leer bleiben — dann ist der Verwalter gemeint,
 *  wie früher, als es nur ihn gab. */
export async function login(password: string, username = ""): Promise<LoginResult> {
  const hash = await passwortAbleiten(username, password);
  const r = await api<LoginResult>("/auth/login", {
    method: "POST",
    // Entweder die Ableitung oder — wo der Browser sie nicht rechnen kann —
    // das Passwort selbst. Nie beides.
    body: JSON.stringify(hash
      ? { username, password_hash: hash }
      : { username, password }),
  });
  if (!isChallenge(r)) {
    setToken(r.token);
    await ladeMich();
  }
  return r;
}

/** Sich selbst anmelden. Danach wartet man auf die Freigabe. */
export async function registrieren(
  username: string, password: string, displayName = "",
): Promise<void> {
  const hash = await passwortAbleiten(username, password);
  await api("/auth/register", {
    method: "POST",
    body: JSON.stringify({
      username,
      display_name: displayName,
      ...(hash ? { password_hash: hash } : { password }),
    }),
  });
}

/** Das eigene Passwort ändern. Beide Seiten werden mit dem eigenen Namen
 *  abgeleitet — das Salz hängt daran. */
export async function passwortAendern(alt: string, neu: string): Promise<void> {
  const name = getMich()?.username ?? "";
  const altH = await passwortAbleiten(name, alt);
  const neuH = await passwortAbleiten(name, neu);
  await api("/auth/password", {
    method: "POST",
    body: JSON.stringify(altH && neuH
      ? { old_password_hash: altH, new_password_hash: neuH }
      : { old_password: alt, new_password: neu }),
  });
}

export async function twoFactorLogin(challenge: string, code: string): Promise<LoginOk> {
  const r = await api<LoginOk>("/auth/2fa/login", {
    method: "POST",
    body: JSON.stringify({ challenge, code }),
  });
  setToken(r.token);
  await ladeMich();
  return r;
}

/* ─────────────────── Wer ist angemeldet, und was darf er ───────────────────
 *
 * Bis hierher gab es nur „angemeldet: ja/nein" — es gab ja auch nur einen
 * Menschen. Jetzt entscheidet der Server je Benutzer, und die Oberfläche
 * zeigt nur, was er darf. Das ist Höflichkeit, keine Sicherheit: die liegt
 * im Server, der jede Anfrage einzeln prüft.
 */

export type Ich = {
  id: string;
  username: string;
  display_name: string;
  is_admin: boolean;
  approved: boolean;
  permissions: string[];
  /** Was ein Verwalter überhaupt vergeben kann. */
  grantable: string[];
  /** Was beim Verwalter bleibt — sein Postfach, seine Noten. */
  admin_only: string[];
  /** Die Rolle sagt, wer verwalten darf; das hier, ob gerade verwaltet
   *  wird. Siehe adminModusStarten. */
  admin_mode?: AdminModus;
};

/** „Darf ich verwalten, tue ich es gerade, und bis wann?" */
export type AdminModus = {
  active: boolean;
  may: boolean;
  max_hours?: number;
  expires_at?: string;
};

const ICH_KEY = "home.ich";
let ich: Ich | null = (() => {
  // Aus dem Speicher vorladen, damit der erste Bildaufbau schon die richtige
  // Leiste zeigt, statt sie nach der Antwort umzubauen.
  try {
    const roh = localStorage.getItem(ICH_KEY);
    const o: unknown = roh ? JSON.parse(roh) : null;
    // Dieselbe Pruefung wie bei der Antwort vom Server, aus demselben
    // Grund: ein alter oder halber Stand im Speicher darf den ersten
    // Bildaufbau nicht umwerfen.
    return siehtNachMirAus(o) ? o : null;
  } catch { return null; }
})();
const ichHoerer = new Set<() => void>();

export function onIchChange(fn: () => void): () => void {
  ichHoerer.add(fn);
  return () => { ichHoerer.delete(fn); };
}

export function getMich(): Ich | null { return ich; }

function setzeMich(neu: Ich | null) {
  ich = neu;
  try {
    if (neu) localStorage.setItem(ICH_KEY, JSON.stringify(neu));
    else localStorage.removeItem(ICH_KEY);
  } catch { /* egal */ }
  for (const fn of ichHoerer) fn();
}

/** Sieht die Antwort ueberhaupt nach einer Auskunft ueber jemanden aus?
 *
 *  Der Aufruf gibt `Ich` zurueck — auf dem Papier. In Wirklichkeit steht
 *  zwischen hier und der Datenbank ein Vorbau, und der antwortet waehrend
 *  eines Aufspielens auch schon mal mit einer Fehlerseite und Status 200.
 *  Faellt das durch, steht gleich darauf `permissions.includes` auf
 *  `undefined` — mitten im Aufbau der Leiste, also ausserhalb jeder
 *  Seitengrenze, und die ganze Anwendung zeigt „Something went wrong".
 *
 *  Ein Feld zu pruefen genuegt: was keine Rechteliste hat, ist keine
 *  Auskunft. */
function siehtNachMirAus(o: unknown): o is Ich {
  return !!o && typeof o === "object"
    && Array.isArray((o as { permissions?: unknown }).permissions);
}

/** Beim Server nachfragen, wer man ist. */
export async function ladeMich(): Promise<Ich | null> {
  if (!getToken()) { setzeMich(null); return null; }
  try {
    const antwort = await api<Ich>("/auth/whoami");
    if (siehtNachMirAus(antwort)) setzeMich(antwort);
  } catch {
    // Ein Fehler hier darf niemanden aussperren: die Seiten prüfen ohnehin
    // beim Server nach. Der alte Stand bleibt stehen.
  }
  return ich;
}

/** Ob der angemeldete Benutzer eine Berechtigung hat.
 *
 *  Ohne Auskunft (alter Server, Abruf noch unterwegs) gilt „darf" — sonst
 *  stünde nach einer Aktualisierung kurz eine leere Leiste da. Der Server
 *  weist trotzdem ab, was nicht erlaubt ist. */
export function darf(perm: string): boolean {
  if (!perm) return true;
  if (!ich) return true;
  if (ich.is_admin) return true;
  return ich.permissions.includes(perm);
}

type StepUpOk = { token: string; expires_in: number };

/**
 * Exchange the account password for a short-lived step-up token. No 2FA code:
 * the session already proves a full (2FA-included) login happened, so the
 * backend only re-checks the password. Re-login is required once the session
 * itself expires.
 */
export async function stepUp(password: string): Promise<string> {
  const r = await api<StepUpOk>("/auth/stepup", {
    method: "POST",
    body: JSON.stringify({ password }),
  });
  // Re-use the token for the rest of its life (minus a safety margin) so
  // consecutive sensitive actions don't re-prompt.
  cachedStepUp = { token: r.token, exp: Date.now() + Math.max(0, r.expires_in - 15) * 1000 };
  return r.token;
}

/** Kill-switch: invalidate every outstanding session (this one included). */
export async function revokeSessions(): Promise<void> {
  await api("/auth/sessions/revoke", { method: "POST" });
}

/* ─────────────────────────── Verwaltermodus ───────────────────────────
 *
 * Die Rolle hat man; die Vollmacht holt man sich. Passwort, zweiter Faktor,
 * ein bis drei Stunden — danach ist man wieder ein gewöhnlicher Benutzer,
 * ohne etwas zu tun. Jede Erhebung steht im Protokoll der Konsole.
 */

export async function adminModus(): Promise<AdminModus> {
  return api<AdminModus>("/auth/admin-mode");
}

export async function adminModusStarten(
  passwort: string, code: string, stunden: number,
): Promise<AdminModus> {
  const benutzer = getMich()?.username ?? "";
  // Dieselbe Ableitung wie bei der Anmeldung: das Passwort selbst bleibt
  // auf dem Gerät. Kann der Browser das nicht, geht es wie bisher.
  const hash = await passwortAbleiten(benutzer, passwort);
  const r = await api<AdminModus>("/auth/admin-mode", {
    method: "POST",
    body: JSON.stringify(
      hash ? { password_hash: hash, code, hours: stunden }
           : { password: passwort, code, hours: stunden }),
  });
  await ladeMich();
  return r;
}

export async function adminModusBeenden(): Promise<void> {
  await api("/auth/admin-mode", { method: "DELETE" });
  await ladeMich();
}
